import TableCellRenderer from '@cp/src/components/TableCellRenderer';
import CellRendererShortNumber from '@cp/src/components/TableCellRenderer/CellRendererShortNumber';
import { ColDefsType } from '@navi/web-ui/lib/components/AgTable/types';
import { GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { formatNumber, pluralisation, toFixedValueNotation } from 'src/utils/commonUtils';

export const handleGridReady = (
  event: GridReadyEvent<any>,
  gridRef: any,
  sizeColumnsToFit = true
) => {
  if (sizeColumnsToFit) {
    gridRef.current = event;
    event.api.sizeColumnsToFit();
  }
};

const tooltipContentClass = 'z-[var(--z-index-external-sensei-tooltip)]';

const secondsToHM = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (!hours && !minutes) return { minutes: '0 min' };

  const hoursData = hours > 0 ? pluralisation(`${hours} hr`, hours, 's') : '';
  const minutesData = minutes > 0 ? pluralisation(`${minutes} min`, minutes, 's') : '';
  return { hours: hoursData, minutes: minutesData };
};

export const DPD_BUCKET_COLUMNS = [
  {
    headerName: 'Bucket',
    field: 'dpdBucket',
    width: 120,
    cellRenderer: (params: ICellRendererParams) => {
      const { dpdBucket } = params.data || {};
      return <TableCellRenderer value={dpdBucket} />;
    }
  },
  {
    headerName: 'Performance band',
    field: 'performanceBand',
    width: 140,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { performanceBand } = params?.data || {};
      return <TableCellRenderer value={performanceBand} />;
    }
  },
  {
    headerName: 'LANs allocated',
    field: 'lansAllocated',
    width: 110,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { lansAllocated } = params?.data || {};
      const parsedValue = formatNumber(lansAllocated ?? 0);
      return <TableCellRenderer value={parsedValue} />;
    }
  },
  {
    headerName: 'Agent allocation',
    field: 'agentAllocationPercentage',
    width: 120,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { agentAllocationPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(agentAllocationPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'EMI OD',
    field: 'emiOverdueAmount',
    width: 100,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { emiOverdueAmount } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={emiOverdueAmount}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Target CE',
    field: 'targetCEPercentage',
    width: 90,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { targetCEPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(targetCEPercentage);
      return (
        <TableCellRenderer
          value={parsedValue ? `${parsedValue}%` : ''}
          toolTipContent={parsedValue ? `${parsedValue}%` : ''}
          showToolTip={!!parsedValue}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Target OD amount',
    field: 'targetOverdueAmount',
    width: 115,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { targetOverdueAmount } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={targetOverdueAmount}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'EMI CE',
    field: 'emiCEPercentage',
    width: 80,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { emiCEPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(emiCEPercentage);
      return (
        <TableCellRenderer
          value={parsedValue ? `${parsedValue}%` : ''}
          toolTipContent={parsedValue ? `${parsedValue}%` : ''}
          showToolTip={!!parsedValue}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'EMI OD collected',
    field: 'emiOverdueCollected',
    width: 110,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { emiOverdueCollected } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={emiOverdueCollected}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Achievement',
    field: 'achievementPercentage',
    width: 130,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { achievementPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(achievementPercentage);
      return (
        <TableCellRenderer
          value={parsedValue ? `${parsedValue}%` : ''}
          ellipsisWrapperClass={
            achievementPercentage >= 100 ? 'text-[var(--green-base)]' : 'text-[var(--red-base)]'
          }
        />
      );
    }
  },
  {
    headerName: 'LMSD achievement',
    field: 'lmsdAchievedPercentage',
    width: 125,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { lmsdAchievedPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(lmsdAchievedPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Non-Enach cash collected',
    field: 'nonEnachCashCollected',
    width: 140,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { nonEnachCashCollected } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={nonEnachCashCollected}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  }
] as ColDefsType[];

export const AGENCY_PERFORMANCE_COLUMNS = [
  {
    headerName: 'Agent',
    field: 'agentName',
    width: 200,
    cellRenderer: (params: ICellRendererParams) => {
      const { agentName, agencyName } = params.data || {};
      return (
        <div className="flex flex-col">
          <TableCellRenderer
            value={agentName}
            toolTipContent={agentName}
            showToolTip
            tooltipContentClass={tooltipContentClass}
          />
          <div className="text-[--navi-color-gray-c3] text-xs">
            <TableCellRenderer
              value={agencyName}
              toolTipContent={agencyName}
              showToolTip
              tooltipContentClass={tooltipContentClass}
            />
          </div>
        </div>
      );
    }
  },
  {
    headerName: 'LANs allocated',
    field: 'totalAllocatedLans',
    width: 90,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { totalAllocatedLans } = params?.data || {};
      const parsedValue = formatNumber(totalAllocatedLans ?? 0);
      return <TableCellRenderer value={parsedValue} />;
    }
  },
  {
    headerName: 'Bucket 1&2 coverage',
    field: 'coverageThirtyOneNinetyLansPercent',
    width: 100,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { coverageThirtyOneNinetyLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(coverageThirtyOneNinetyLansPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Bucket 3&4 coverage',
    field: 'coverageNinetyPlusLansPercent',
    width: 90,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { coverageNinetyPlusLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(coverageNinetyPlusLansPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Bucket 1&2 genuine coverage',
    field: 'genuineCoverageThirtyOneNinetyLansPercent',
    width: 110,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { genuineCoverageThirtyOneNinetyLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(genuineCoverageThirtyOneNinetyLansPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Bucket 3&4 genuine coverage',
    field: 'genuineCoverageNinetyPlusLansPercent',
    width: 110,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { genuineCoverageNinetyPlusLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(genuineCoverageNinetyPlusLansPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Visits / day',
    field: 'visitsPerDay',
    width: 80,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { visitsPerDay } = params?.data || {};
      const parsedValue = toFixedValueNotation(visitsPerDay);
      return <TableCellRenderer value={parsedValue} />;
    }
  },
  {
    headerName: 'Suspicious feedback %',
    field: 'suspiciousFeedbackPercent',
    width: 90,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { suspiciousFeedbackPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(suspiciousFeedbackPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'PTPs generated',
    field: 'ptpsGenerated',
    width: 100,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { ptpsGenerated, ptpsGeneratedPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(ptpsGeneratedPercent);
      return (
        <div className="flex flex-col">
          <TableCellRenderer value={ptpsGenerated} />
          <div className="text-[--navi-color-gray-c3] text-xs">
            <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />
          </div>
        </div>
      );
    }
  },
  {
    headerName: 'Broken PTP',
    field: 'brokenPtpPercentage',
    width: 70,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { brokenPtpPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(brokenPtpPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Distance travelled / day',
    field: 'distanceTravelledPerDay',
    width: 110,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { distanceTravelledPerDay } = params?.data || {};
      const parsedValue = toFixedValueNotation(distanceTravelledPerDay);
      return (
        <TableCellRenderer
          value={
            parsedValue
              ? pluralisation(
                  `${formatNumber(Number(parsedValue))} km`,
                  distanceTravelledPerDay,
                  's'
                )
              : ''
          }
        />
      );
    }
  },
  {
    headerName: ' App usage time / day',
    field: 'appUsagePerDay',
    width: 110,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { appUsagePerDay } = params?.data || {};
      const { hours, minutes } = secondsToHM(appUsagePerDay);
      return (
        <div className="flex flex-col">
          {hours ? <TableCellRenderer value={hours} /> : <div />}
          <div style={{ lineHeight: '1rem' }}>
            <TableCellRenderer value={minutes} />
          </div>
        </div>
      );
    }
  }
] as ColDefsType[];

export const TODAY_AGENCY_PERFORMANCE_COLUMNS = [
  {
    headerName: 'Agent',
    field: 'agentName',
    width: 240,
    cellRenderer: (params: ICellRendererParams) => {
      const { agentName, agencyName } = params.data || {};
      return (
        <div className="flex flex-col">
          <TableCellRenderer value={agentName} toolTipContent={agentName} showToolTip />
          <div className="text-[--navi-color-gray-c3] text-xs">
            <TableCellRenderer value={agencyName} toolTipContent={agencyName} showToolTip />
          </div>
        </div>
      );
    }
  },
  {
    headerName: 'Visits',
    field: 'visitsToday',
    width: 160,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { visitsToday } = params?.data || {};
      return <TableCellRenderer value={visitsToday} />;
    }
  },
  {
    headerName: 'Today’s PTP',
    field: 'ptpsToday',
    width: 180,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { ptpsToday } = params?.data || {};
      return <TableCellRenderer value={ptpsToday} />;
    }
  },
  {
    headerName: 'Today’s PTP visits',
    field: 'ptpsVisitedToday',
    width: 150,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { ptpsVisitedToday } = params?.data || {};
      return <TableCellRenderer value={ptpsVisitedToday} />;
    }
  },
  {
    headerName: 'Partially/Fully Paid Accounts',
    field: 'accountsPaidToday',
    width: 150,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { accountsPaidToday } = params?.data || {};
      return <TableCellRenderer value={accountsPaidToday} />;
    }
  },
  {
    headerName: 'OD collected',
    field: 'odCollectedToday',
    width: 160,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { odCollectedToday } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={odCollectedToday}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Total cash collected',
    field: 'cashCollectedToday',
    width: 160,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { cashCollectedToday } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={cashCollectedToday}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  }
] as ColDefsType[];

export const AGENCY_DETAILS_COLUMNS = [
  {
    headerName: 'Agency',
    field: 'agencyName',
    width: 200,
    cellRenderer: (params: ICellRendererParams) => {
      const { agencyName } = params.data || {};
      return (
        <TableCellRenderer
          value={agencyName}
          toolTipContent={agencyName}
          showToolTip
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Bucket 1&2 coverage',
    field: 'coverageThirtyOneNinetyLansPercent',
    width: 90,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { coverageThirtyOneNinetyLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(coverageThirtyOneNinetyLansPercent);
      return (
        <TableCellRenderer
          value={parsedValue ? `${parsedValue}%` : ''}
          toolTipContent={`${parsedValue}%`}
          showToolTip={!!parsedValue}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Bucket 3&4 coverage',
    field: 'coverageNinetyPlusLansPercent',
    width: 90,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { coverageNinetyPlusLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(coverageNinetyPlusLansPercent);
      return (
        <TableCellRenderer
          value={parsedValue ? `${parsedValue}%` : ''}
          toolTipContent={`${parsedValue}%`}
          showToolTip={!!parsedValue}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Bucket 1&2 genuine coverage',
    field: 'genuineCoverageThirtyOneNinetyLansPercent',
    width: 90,
    wrapHeaderText: true,
    wrapText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { genuineCoverageThirtyOneNinetyLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(genuineCoverageThirtyOneNinetyLansPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Bucket 3&4 genuine coverage',
    field: 'genuineCoverageNinetyPlusLansPercent',
    width: 110,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { genuineCoverageNinetyPlusLansPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(genuineCoverageNinetyPlusLansPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Visits / agent / day',
    field: 'visitsPerDayPerAgent',
    width: 120,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { visitsPerDayPerAgent } = params?.data || {};
      const parsedValue = toFixedValueNotation(visitsPerDayPerAgent);
      return <TableCellRenderer value={parsedValue} />;
    }
  },
  {
    headerName: 'Suspicious feedback %',
    field: 'suspiciousFeedbackPercent',
    width: 120,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { suspiciousFeedbackPercent } = params?.data || {};
      const parsedValue = toFixedValueNotation(suspiciousFeedbackPercent);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'PTPs generated',
    field: 'ptpsGenerated',
    width: 110,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { ptpsGenerated } = params?.data || {};
      return (
        <div className="flex flex-col">
          <TableCellRenderer value={ptpsGenerated} />
        </div>
      );
    }
  },
  {
    headerName: 'Broken PTP',
    field: 'brokenPtpPercentage',
    width: 90,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { brokenPtpPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(brokenPtpPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Distance travelled / agent / day',
    field: 'distanceTravelledPerDayPerAgent',
    width: 140,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { distanceTravelledPerDayPerAgent } = params?.data || {};
      const parsedValue = toFixedValueNotation(distanceTravelledPerDayPerAgent);
      return (
        <TableCellRenderer
          value={
            parsedValue
              ? pluralisation(
                  `${formatNumber(Number(parsedValue))} km`,
                  distanceTravelledPerDayPerAgent,
                  's'
                )
              : ''
          }
        />
      );
    }
  },
  {
    headerName: 'App usage time / agent / day',
    field: 'appUsagePerDayPerAgent',
    width: 135,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { appUsagePerDayPerAgent } = params?.data || {};
      const { hours, minutes } = secondsToHM(appUsagePerDayPerAgent);
      return (
        <div className="flex flex-col">
          {hours ? <TableCellRenderer value={hours} /> : null}
          <div className="leading-4">
            <TableCellRenderer value={minutes} />
          </div>
        </div>
      );
    }
  }
] as ColDefsType[];

export const TODAY_AGENCY_DETAILS_COLUMNS = [
  {
    headerName: 'Agency',
    field: 'agencyName',
    width: 240,
    cellRenderer: (params: ICellRendererParams) => {
      const { agencyName } = params.data || {};
      return (
        <TableCellRenderer
          value={agencyName}
          toolTipContent={agencyName}
          showToolTip
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Visits/agent',
    field: 'visitsTodayPerAgent',
    width: 160,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { visitsTodayPerAgent } = params?.data || {};
      const parsedValue = toFixedValueNotation(visitsTodayPerAgent);
      return <TableCellRenderer value={parsedValue} />;
    }
  },
  {
    headerName: 'Today’s PTP accounts',
    field: 'totalPtpsToday',
    width: 180,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { totalPtpsToday } = params?.data || {};
      return <TableCellRenderer value={totalPtpsToday} />;
    }
  },
  {
    headerName: 'Today’s PTP visits',
    field: 'totalPtpsVisitedToday',
    width: 150,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { totalPtpsVisitedToday } = params?.data || {};
      return <TableCellRenderer value={totalPtpsVisitedToday} />;
    }
  },
  {
    headerName: 'Partially / Fully Paid Accounts',
    field: 'totalAccountsPaidToday',
    width: 150,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { totalAccountsPaidToday } = params?.data || {};
      return <TableCellRenderer value={totalAccountsPaidToday} />;
    }
  },
  {
    headerName: 'OD collected',
    field: 'totalOdCollectedToday',
    width: 160,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { totalOdCollectedToday } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={totalOdCollectedToday}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Total cash collected',
    field: 'totalCashCollectedToday',
    width: 160,
    wrapHeaderText: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { totalCashCollectedToday } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={totalCashCollectedToday}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  }
] as ColDefsType[];

export const AGENT_PERFORMANCE_COLUMNS = [
  {
    field: 'agentName',
    headerName: 'Agent Name',
    width: 240,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { agentName } = params.data || {};
      return (
        <TableCellRenderer
          value={agentName}
          toolTipContent={agentName}
          showToolTip
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    field: 'lansAllocated',
    headerName: 'LANs allocated',
    width: 120,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { lansAllocated } = params?.data || {};
      const parsedValue = formatNumber(lansAllocated ?? 0);
      return <TableCellRenderer value={parsedValue} />;
    }
  },
  {
    headerName: 'EMI OD',
    field: 'emiOverdueAmount',
    width: 120,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { emiOverdueAmount } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={emiOverdueAmount}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Target CE',
    field: 'targetCEPercentage',
    width: 120,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { targetCEPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(targetCEPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Target OD amount',
    field: 'targetOverdueAmount',
    width: 120,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { targetOverdueAmount } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={targetOverdueAmount}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'EMI CE',
    field: 'emiCEPercentage',
    width: 120,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { emiCEPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(emiCEPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'EMI OD collected',
    field: 'emiOverdueCollected',
    width: 130,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { emiOverdueCollected } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={emiOverdueCollected}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  },
  {
    headerName: 'Achievement',
    field: 'achievementPercentage',
    width: 140,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { achievementPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(achievementPercentage);
      return (
        <TableCellRenderer
          value={parsedValue ? `${parsedValue}%` : ''}
          ellipsisWrapperClass={
            achievementPercentage >= 100 ? 'text-[var(--green-base)]' : 'text-[var(--red-base)]'
          }
        />
      );
    }
  },
  {
    headerName: 'LMSD achievement',
    field: 'lmsdAchievedPercentage',
    width: 140,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { lmsdAchievedPercentage } = params?.data || {};
      const parsedValue = toFixedValueNotation(lmsdAchievedPercentage);
      return <TableCellRenderer value={parsedValue ? `${parsedValue}%` : ''} />;
    }
  },
  {
    headerName: 'Non-Enach cash collected',
    field: 'nonEnachCashCollected',
    width: 160,
    wrapHeaderText: true,
    sortable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { nonEnachCashCollected } = params?.data || {};
      return (
        <CellRendererShortNumber
          value={nonEnachCashCollected}
          isCurrency
          significantDigits={2}
          tooltipContentClass={tooltipContentClass}
        />
      );
    }
  }
] as ColDefsType[];
